import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceLayout from "@/components/ServiceLayout";
import PhaseHero from "@/components/how-it-works/PhaseHero";
import StrategySection from "@/components/strategy/StrategySection";
import { Brain } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const serviceStrategyImg = "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/services-heroes/Graphics-23.png";

const strategySections = [
  {
    title: "Specialized Scorecards",
    description: "Deploy multi-dimensional scorecards to assess your organization's readiness across key transformation dimensions.",
    processes: [
      { id: 1, text: "Digital Transformation Readiness assessment" },
      { id: 2, text: "MaaS (Machine-as-a-Service) Readiness evaluation" },
      { id: 3, text: "RaaS (Robot-as-a-Service) Readiness scoring" },
      { id: 4, text: "Performance-Based Contracting Readiness analysis" },
      { id: 5, text: "Custom scorecard design for specific business models" },
    ],
    outcomes: [
      { id: 1, text: "Readiness scores across key dimensions" },
      { id: 2, text: "Benchmarking against industry standards" },
      { id: 3, text: "Quantified maturity levels per domain" },
    ],
    timeframe: "2 - 4 WEEKS",
  },
  {
    title: "Evaluation Workflows",
    description: "Manage structured evaluation workflows that aggregate input from multiple stakeholders into actionable data.",
    processes: [
      { id: 1, text: "Intake data collection from teams and stakeholders" },
      { id: 2, text: "Structured assessment submissions" },
      { id: 3, text: "Multi-stakeholder input aggregation" },
      { id: 4, text: "Cross-functional alignment analysis" },
      { id: 5, text: "Operational data integration" },
    ],
    outcomes: [
      { id: 1, text: "Consolidated stakeholder insights" },
      { id: 2, text: "Organizational alignment map" },
      { id: 3, text: "Structured input for AI analysis" },
    ],
    timeframe: "3 - 5 WEEKS",
  },
  {
    title: "AI Consultant Layer",
    description: "Leverage our AI-powered consultant to analyze responses using proprietary playbooks and detect misalignment, risks, and gaps.",
    processes: [
      { id: 1, text: "Analyze responses using proprietary playbooks" },
      { id: 2, text: "Detect organizational misalignment and blind spots" },
      { id: 3, text: "Identify risks and capability gaps" },
      { id: 4, text: "Generate structured insights and patterns" },
      { id: 5, text: "Produce actionable recommendations" },
    ],
    outcomes: [
      { id: 1, text: "Identified performance gaps and risks" },
      { id: 2, text: "Prioritized improvement areas" },
      { id: 3, text: "AI-generated diagnostic insights" },
    ],
    timeframe: "1 - 2 WEEKS",
  },
  {
    title: "Diagnostic Outputs",
    description: "Receive comprehensive diagnostic reports with readiness scores, gap analysis, and strategic recommendations linked to your frameworks.",
    processes: [
      { id: 1, text: "Readiness score compilation across all dimensions" },
      { id: 2, text: "Gap analysis and risk prioritization" },
      { id: 3, text: "Strategic recommendation generation" },
      { id: 4, text: "Framework-linked action planning" },
      { id: 5, text: "Executive summary and presentation" },
    ],
    outcomes: [
      { id: 1, text: "AI-generated diagnostic report" },
      { id: 2, text: "Strategic recommendations linked to your frameworks" },
      { id: 3, text: "Prioritized transformation roadmap" },
    ],
    timeframe: "1 - 2 WEEKS",
  },
];

const AIStrategy = () => {
  useDocumentTitle(
    "Discovery — Advisory | Klexu",
    "Discovery diagnostics to align stakeholders, surface operational signals, and define measurable outcomes for performance-based models."
  );
  return (
    <>
      <Navbar />
      <PhaseHero
        number="01"
        eyebrow="Discovery"
        titleLead="Diagnose before you"
        titleHighlight="prescribe."
        body="Before any transformation, you need an honest picture of where you are. Our Audit assesses your operations, procurement practices, and contracting models against 50+ industry playbooks — identifying where the fault lines are, what's measurable, and what performance-based models are viable for your specific context."
        whatYouGet="A diagnostic report. A gap analysis against industry benchmarks. A prioritized list of procurement categories ready to move to performance-based models."
        whoItsFor="Operations and procurement leaders who suspect the current model isn't working but need evidence before making the case internally."
        ctaLabel="Start with the Audit"
        image={serviceStrategyImg}
      />
      <ServiceLayout title="Discovery" description="" icon={<Brain className="h-8 w-8 text-accent" />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {strategySections.map((section, index) => (
            <StrategySection
              key={index}
              title={section.title}
              description={section.description}
              processes={section.processes}
              outcomes={section.outcomes}
              timeframe={section.timeframe}
            />
          ))}
        </div>
        <Separator className="my-12 bg-white/10" />
        
        <div className="mt-6">
          <h3 className="text-2xl font-bold mb-6">What Makes This Different</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="glass-card border-white/10 bg-white/5 p-6 rounded-xl">
              <h4 className="text-lg font-semibold mb-3 text-muted-foreground">Instead of</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Generic consulting interviews</li>
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Static assessments</li>
                <li className="flex items-start gap-2"><span className="text-red-400">✕</span> Subjective opinions</li>
              </ul>
            </div>
            <div className="glass-card border-white/10 bg-white/5 p-6 rounded-xl">
              <h4 className="text-lg font-semibold mb-3 text-foreground">You get</h4>
              <ul className="space-y-2 text-foreground">
                <li className="flex items-start gap-2"><span className="text-green-400">✓</span> Multi-dimensional scorecards</li>
                <li className="flex items-start gap-2"><span className="text-green-400">✓</span> AI-assisted analysis</li>
                <li className="flex items-start gap-2"><span className="text-green-400">✓</span> Proprietary playbook intelligence</li>
              </ul>
            </div>
          </div>
          
          <blockquote className="border-l-4 border-accent pl-6 py-4 my-8">
            <p className="text-xl font-semibold italic text-foreground">
              "We quantify what most organizations only describe."
            </p>
          </blockquote>
          
          <p className="text-muted-foreground mb-4">
            We move beyond interviews and intuition by transforming organizational input into quantifiable insights and actionable diagnostics. Through specialized scorecards, structured evaluation workflows, and our AI Consultant layer, we deliver readiness scores, gap analysis, organizational alignment maps, and strategic recommendations — all linked to your existing frameworks and business models.
          </p>
          
          <a
            href="https://studio.luisprato.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 transition-colors hover:bg-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Open Contract Studio in a new tab"
          >
            <Brain className="h-4 w-4 text-accent" />
            <span className="text-sm font-medium text-accent">Powered by Contract Studio™</span>
          </a>
        </div>
      </ServiceLayout>
      <Footer />
    </>
  );
};
export default AIStrategy;
